// storageSingleton.ts

import _ from 'lodash';
import {
  decryptFeKeyWithPrivateKey,
  encryptFeKeyWithPubKey,
  generateFeKey,
  getKeyPair,
} from '../cryptor/keyPairCrypto';
import {
  getUserEncFeKey,
  saveUserEncFeKey,
} from '../database/user/userStorage';

interface StorageData {
  [key: string]: CryptoKey;
}

// type LockQueueItem = (value: unknown) => void;
// interface LockQueue {
//   [id: string]: LockQueueItem[];
// }

// interface LockRecords {
//   [id: string]: true;
// }
export type IAcquiredError = string | undefined;

class SessionStorageSingleton {
  private static instance: SessionStorageSingleton;
  private feKeyStorage: StorageData;
  private privateKey: CryptoKey | undefined;
  private publicKey: CryptoKey | undefined;
  private userId: string;
  private directoryHandler: FileSystemDirectoryHandle | undefined;
  // private lockQueue: LockQueue = {};
  // private lockRecords: LockRecords = {};

  private constructor() {
    this.feKeyStorage = {};
    this.userId = '';
  }

  public static getInstance(): SessionStorageSingleton {
    if (!SessionStorageSingleton.instance) {
      SessionStorageSingleton.instance = new SessionStorageSingleton();
    }
    return SessionStorageSingleton.instance;
  }

  public initialize(
    userId: string,
    privateKey: CryptoKey,
    publicKey: CryptoKey,
    directoryHandle?: FileSystemDirectoryHandle | undefined
  ) {
    this.userId = userId;
    this.privateKey = privateKey;
    this.publicKey = publicKey;
    this.directoryHandler = directoryHandle;
  }

  public getUserId(): string {
    return this.userId;
  }

  public setFeKey(key: string, value: CryptoKey): void {
    this.feKeyStorage[key] = value;
  }

  public async getFeKey(key: string): Promise<CryptoKey | undefined> {
    try {
      if (!this.feKeyStorage[key] && this.privateKey) {
        const encfeKey = await getUserEncFeKey(this.userId, key);
        const feKey = await decryptFeKeyWithPrivateKey(
          this.privateKey,
          encfeKey
        );
        this.feKeyStorage[key] = feKey;
        return feKey;
      }
      return this.feKeyStorage[key];
    } catch (err) {
      return undefined;
    }
  }

  public async generateAndSaveFeKey(feKeyId: string, key?: CryptoKey) {
    try {
      if (!this.publicKey) return undefined;
      const feKey = key ?? (await generateFeKey());
      const encFeKey = await encryptFeKeyWithPubKey(this.publicKey, feKey);
      await saveUserEncFeKey(this.userId, encFeKey, feKeyId);
      this.feKeyStorage[feKeyId] = feKey;
      return feKey;
    } catch (err) {
      console.log('err', err);
      return undefined;
    }
  }

  public removeFeKey(key: string): void {
    delete this.feKeyStorage[key];
  }

  public reset(): void {
    this.feKeyStorage = {};
    this.privateKey = undefined;
    this.publicKey = undefined;
    this.directoryHandler = undefined;
  }

  public async getPrivateKey(): Promise<CryptoKey> {
    const keys = await getKeyPair();
    return this.privateKey ?? keys.privateKey;
  }
  public async getPublicKey(): Promise<CryptoKey> {
    const keys = await getKeyPair();
    return this.publicKey ?? keys.publicKey;
  }
  public getDirectoryHandler() {
    return this.directoryHandler;
  }

  private singleAcquired: Map<string, (value: unknown) => void> = new Map();
  private acquired: Array<(value: unknown) => void> = [];
  private generateAcquiredKey = (id: string, type: 'r' | 'd') =>
    `${id}-${type}`;
  public async acquire(
    id: string,
    type: 'r' | 'd', // raw: raw | d : draft,
    ifAvailable = true,
    singleAcquired = false
  ): Promise<IAcquiredError> {
    const errMsg = `Sorry, but the record with ID ${id} is currently in use by another process or tab. Please attempt your operation again once the record becomes available.`;
    let resolve;
    const p = new Promise((res, rej) => {
      resolve = res;
    });

    const key = this.generateAcquiredKey(id, type);

    let error: IAcquiredError = '';
    try {
      const lockOptions = {
        ifAvailable: ifAvailable,
      };
      await new Promise((resolve, reject) => {
        navigator.locks.request(key, lockOptions, (lock) => {
          if (!lock) {
            reject(new Error('Lock acquisition failed'));
          } else {
            resolve(lock);
            return p;
          }
        });
      });
    } catch {
      return errMsg;
    }

    if (resolve) {
      if (singleAcquired) {
        this.singleAcquired.set(key, resolve);
      } else {
        this.acquired.push(resolve);
      }
    }
    return error;
  }
  public releaseOne(id: string, type: 'r' | 'd') {
    const key = this.generateAcquiredKey(id, type);
    if (this.singleAcquired.has(key)) {
      const resolve = this.singleAcquired.get(key);
      if (resolve) resolve('');
      this.singleAcquired.delete(key);
    }
  }
  public release() {
    this.acquired.forEach((resolve) => {
      resolve('');
    });
  }
}

const sessionStorageSingleton = SessionStorageSingleton.getInstance();
export default sessionStorageSingleton;
